import React, { Fragment, useEffect, useState } from 'react';

import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useInfiniteQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import AddIcon from 'icons/add.svg';
import DragIndicatorIcon from 'icons/drag-indicator.svg';
import FilesIcon from 'icons/files.svg';
import FoldersIcon from 'icons/folders.svg';
import LockIcon from 'icons/ic-lock-20.svg';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import voca from 'voca';

import { useUpdateFavouriteUserFolderMutation } from 'components/hooks/favouriteUserFolderMutations';

import InfoCardLink from 'components/application/cards/InfoCardLink';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  Divider,
  Item,
  SidebarMenu,
  Subsection,
  SubsectionItem,
} from 'components/application/SidebarMenu';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import { useBreadBoard } from 'components/contexts/Toaster';

import {
  formatInfiniteData,
  getNextPageParam,
} from 'components/helpers/reactQuery';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function ProjectsDashboardSidebarMenu({
  activeSection,
  hasFolders,
  onFolderClick,
  onNewFolderClick,
  onSectionClick,
  selectedFolderId,
}) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();

  const { folderPolicy } = useProjectsDashboardContext();

  const [currentFavouriteUserFolderIds, setCurrentFavouriteUserFolderIds] =
    useState([]);

  const [draggingFavouriteUserFolderId, setDraggingFavouriteUserFolderId] =
    useState(null);

  const sensors = useSensors(useSensor(PointerSensor));

  const [loadMoreFavouritesRef, isLoadMoreFavouritesInView] = useInView();
  const [loadMoreRecentsRef, isLoadMoreRecentsInView] = useInView();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsDashboard.components.ProjectsDashboardSidebarMenu',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const updateFavouriteUserFolderMutation =
    useUpdateFavouriteUserFolderMutation();

  const {
    data: domainFavouriteUserFolders,
    fetchNextPage: fetchDomainFavouriteUserFoldersNextPage,
    hasNextPage: hasDomainFavouriteUserFoldersNextPage,
    isFetching: isFetchingDomainFavouriteUserFolders,
    isSuccess: isDomainFavouriteUserFoldersFetchSuccess,
  } = useInfiniteQuery({
    enabled: folderPolicy.canIndex && hasFolders,
    getNextPageParam,
    keepPreviousData: true,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axios.get('/favourite_user_folders', {
        params: { page: pageParam },
      });

      return response.data;
    },
    queryKey: ['folders', 'favourites'],
    select: (data) => formatInfiniteData({ data, resourceTypes: ['folder'] }),
  });

  const {
    data: recentUserFolderSelections,
    fetchNextPage: fetchRecentUserFolderSelectionsNextPage,
    hasNextPage: hasRecentUserFolderSelectionsNextPage,
    isFetching: isFetchingRecentUserFolderSelections,
    isSuccess: isRecentUserFolderSelectionsFetchSuccess,
  } = useInfiniteQuery({
    enabled: folderPolicy.canIndex && hasFolders,
    getNextPageParam,
    keepPreviousData: true,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axios.get('/recent_user_folder_selections', {
        params: { page: pageParam },
      });

      return response.data;
    },
    queryKey: ['folders', 'recents'],
    select: (data) => formatInfiniteData({ data, resourceTypes: ['folder'] }),
  });

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const previousIds = [...currentFavouriteUserFolderIds];
      const oldIndex = previousIds.indexOf(active.id);
      const newIndex = previousIds.indexOf(over.id);

      setCurrentFavouriteUserFolderIds(
        arrayMove(previousIds, oldIndex, newIndex),
      );

      updateFavouriteUserFolderMutation.mutate(
        {
          id: active.id,
          favouriteUserFolderParams: { position: newIndex + 1 },
        },
        {
          onError: () => {
            setCurrentFavouriteUserFolderIds(previousIds);
            breadBoard.addInedibleToast();
          },
        },
      );
    }

    setDraggingFavouriteUserFolderId(null);
  };

  const { pluralRAMSName } = currentActor.division.attributes;

  const capitalizedPluralRAMSName = voca.capitalize(pluralRAMSName);

  const isSorting = !!draggingFavouriteUserFolderId;

  const shouldLoadMoreFavourites =
    currentFavouriteUserFolderIds.length > 0 &&
    hasDomainFavouriteUserFoldersNextPage &&
    isLoadMoreFavouritesInView &&
    !isFetchingDomainFavouriteUserFolders;

  if (shouldLoadMoreFavourites) fetchDomainFavouriteUserFoldersNextPage();

  const shouldLoadMoreRecents =
    recentUserFolderSelections?.data.length > 0 &&
    hasRecentUserFolderSelectionsNextPage &&
    isLoadMoreRecentsInView &&
    !isFetchingRecentUserFolderSelections;

  if (shouldLoadMoreRecents) fetchRecentUserFolderSelectionsNextPage();

  const favouriteUserFolderIds = domainFavouriteUserFolders?.data.map(
    (favouriteUserFolder) => favouriteUserFolder.id,
  );

  const [isAccordionOpen, setIsAccordionOpen] = useState(
    ['current', 'archive'].includes(activeSection),
  );

  useEffect(() => {
    if (favouriteUserFolderIds) {
      setCurrentFavouriteUserFolderIds(favouriteUserFolderIds);
    }
  }, [JSON.stringify(favouriteUserFolderIds)]);

  return (
    <SidebarMenu>
      <div className='tw-flex tw-flex-col tw-p-2'>
        <Accordion
          isOpen={isAccordionOpen}
          triggerContent={
            <AccordionTrigger
              isActive={
                !isAccordionOpen &&
                ['current', 'archive'].includes(activeSection)
              }
              isOpen={isAccordionOpen}
              onClick={() => {
                setIsAccordionOpen((prevState) => !prevState);
                if (!['current', 'archive'].includes(activeSection))
                  onSectionClick({ section: 'current' });
              }}
            >
              <FilesIcon className='tw-flex-none' height={20} width={20} />
              <div className='tw-truncate'>{capitalizedPluralRAMSName}</div>
            </AccordionTrigger>
          }
        >
          <AccordionItem
            isActive={activeSection === 'current'}
            onClick={() => onSectionClick({ section: 'current' })}
          >
            {i18nTCommon('projects.currentProjects', {
              interpolation: { escapeValue: false },
              pluralRAMSName: capitalizedPluralRAMSName,
            })}
          </AccordionItem>
          <AccordionItem
            isActive={activeSection === 'archive'}
            onClick={() => onSectionClick({ section: 'archive' })}
          >
            {i18nTCommon('shared.archive')}
          </AccordionItem>
        </Accordion>
      </div>
      <Divider />
      <div className='tw-mt-2 tw-flex tw-flex-col tw-gap-1 tw-overflow-hidden'>
        {folderPolicy.canIndex ?
          <div className='tw-group tw-relative tw-mx-2 tw-rounded-lg tw-bg-white hover:tw-bg-blue-025'>
            <Item
              isActive={activeSection === 'folders'}
              onClick={() => {
                setIsAccordionOpen(false);
                onSectionClick({ section: 'folders' });
              }}
            >
              <FoldersIcon height={20} width={20} />
              {i18nTCommon('projects.folders')}
            </Item>
            {folderPolicy.canCreate && (
              <button
                className={classNames(
                  'tw-absolute tw-right-2 tw-top-1.5 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border-1 tw-border-solid tw-p-1 group-hover:tw-border-blue-050 group-hover:tw-bg-blue-050 group-hover:hover:tw-border-blue-200 group-hover:hover:tw-bg-blue-050',
                  activeSection === 'folders' ?
                    'tw-border-blue-050 tw-bg-blue-050'
                  : 'tw-border-blue-025 tw-bg-blue-025',
                )}
                onClick={() => {
                  setIsAccordionOpen(false);
                  onNewFolderClick();
                }}
              >
                <AddIcon height={20} width={20} />
                <span className='tw-sr-only'>{i18nT('folders.add')}</span>
              </button>
            )}
          </div>
        : <div className='tw-mx-2 tw-flex tw-items-center tw-justify-between tw-px-2 tw-py-2.5 tw-text-grey-500'>
            <div className='tw-flex tw-items-center tw-gap-2'>
              <FoldersIcon
                className='[&_path]:tw-fill-grey-500'
                height={20}
                width={20}
              />
              <p className='tw-mb-0 tw-text-l tw-font-semibold'>
                {i18nTCommon('projects.folders')}
              </p>
            </div>
            <LockIcon
              className='[&_path]:tw-fill-grey-500'
              height={20}
              width={20}
            />
          </div>
        }
        <div className='tw-flex tw-flex-col tw-gap-1 tw-px-4 tw-py-2'>
          {folderPolicy.canIndex ?
            folderPolicy.canCreate &&
            !hasFolders && (
              <p className='tw-mb-0 tw-text-s tw-tracking-wide tw-text-grey-500'>
                <Trans
                  components={{
                    button: (
                      <button
                        className='tw-border-0 tw-bg-white tw-p-0 tw-font-medium tw-text-blue-500'
                        onClick={() => {
                          setIsAccordionOpen(false);
                          onNewFolderClick();
                        }}
                      />
                    ),
                  }}
                  i18nKey='projectsDashboard.components.ProjectsDashboardSidebarMenu.folders.empty'
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{ pluralRAMSName }}
                />
              </p>
            )
          : <p className='tw-mb-0 tw-text-s tw-tracking-wide tw-text-grey-500'>
              <Trans
                components={{
                  a: (
                    /* eslint-disable jsx-a11y/anchor-has-content */
                    <a
                      className='tw-font-medium tw-text-blue-500'
                      href='https://intercom.help/hands-hq/en/articles/11071322-unlock-more-with-the-small-teams-plan'
                      rel='noopener noreferrer'
                      target='_blank'
                    />
                    /* eslint-enable jsx-a11y/anchor-has-content */
                  ),
                }}
                i18nKey={`projectsDashboard.components.ProjectsDashboardSidebarMenu.folders.${currentActor.user.attributes.accessType === 'primary' ? 'upgrade' : 'speakToAccountOwner'}`}
                ns='components'
              />
            </p>
          }
          <div className='tw-pt-5'>
            <InfoCardLink
              label='learnMore'
              title={i18nT('folders.infoCardTitle')}
              url='https://intercom.help/hands-hq/en/articles/11154401-folders-organise-and-group-your-rams'
            />
          </div>
        </div>
        {folderPolicy.canIndex && hasFolders && (
          <div className='tw-overflow-y-auto tw-px-2'>
            <Subsection title={i18nTCommon('projects.favourites')}>
              {isDomainFavouriteUserFoldersFetchSuccess &&
                (domainFavouriteUserFolders.data.length > 0 ?
                  <>
                    <DndContext
                      collisionDetection={closestCenter}
                      modifiers={[
                        restrictToParentElement,
                        restrictToVerticalAxis,
                      ]}
                      onDragEnd={handleDragEnd}
                      onDragStart={(event) =>
                        setDraggingFavouriteUserFolderId(event.active.id)
                      }
                      sensors={sensors}
                    >
                      <SortableContext
                        items={currentFavouriteUserFolderIds}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className='tw-relative tw-flex tw-flex-col tw-gap-0.5'>
                          {currentFavouriteUserFolderIds.map((id, index) => {
                            const favouriteUserFolder =
                              domainFavouriteUserFolders.data.find(
                                (favouriteUserFolder) =>
                                  favouriteUserFolder.id === id,
                              );

                            if (!favouriteUserFolder) return null;

                            const folder =
                              domainFavouriteUserFolders.included.folder[
                                favouriteUserFolder.relationships.folder.data.id
                              ];

                            const gap = 2;
                            const height = 36;

                            const topStyle =
                              index === 0 ? 0 : index * (gap + height);

                            return (
                              <Fragment key={id}>
                                {isSorting && (
                                  <div
                                    className='tw-absolute tw-h-9 tw-w-full tw-rounded-lg tw-border-1 tw-border-solid tw-border-blue-100 tw-bg-blue-025'
                                    style={{ top: topStyle }}
                                  />
                                )}
                                <SortableFavouriteUserFolder
                                  folder={folder}
                                  id={id}
                                  isActive={
                                    activeSection === 'favourites' &&
                                    selectedFolderId === folder.id
                                  }
                                  isDraggable={
                                    !updateFavouriteUserFolderMutation.isLoading
                                  }
                                  isSorting={isSorting}
                                  onFolderClick={(...args) => {
                                    setIsAccordionOpen(false);
                                    onFolderClick(...args);
                                  }}
                                />
                              </Fragment>
                            );
                          })}
                        </div>
                      </SortableContext>
                    </DndContext>
                    <div ref={loadMoreFavouritesRef} />
                  </>
                : <p className='tw-mb-0 tw-pl-1 tw-text-s tw-tracking-wide tw-text-grey-500'>
                    <Trans
                      components={{
                        button: (
                          <button
                            className='tw-border-0 tw-bg-white tw-p-0 tw-font-medium tw-text-blue-500'
                            onClick={() => {
                              setIsAccordionOpen(false);
                              onSectionClick({ section: 'folders' });
                            }}
                          />
                        ),
                      }}
                      i18nKey='projectsDashboard.components.ProjectsDashboardSidebarMenu.folders.favourites.empty'
                      ns='components'
                    />
                  </p>)}
            </Subsection>
            <Divider />
            <Subsection title={i18nTCommon('projects.recents')}>
              {isRecentUserFolderSelectionsFetchSuccess &&
                (recentUserFolderSelections.data.length > 0 ?
                  <>
                    {recentUserFolderSelections.data.map(
                      (recentUserFolderSelection) => {
                        const folder =
                          recentUserFolderSelections.included.folder[
                            recentUserFolderSelection.relationships.folder.data
                              .id
                          ];

                        return (
                          <SubsectionItem
                            isActive={
                              activeSection === 'recents' &&
                              selectedFolderId === folder.id
                            }
                            key={recentUserFolderSelection.id}
                            onClick={() => {
                              setIsAccordionOpen(false);
                              onFolderClick({
                                folderId: folder.id,
                                section: 'recents',
                              });
                            }}
                          >
                            {folder.attributes.name}
                          </SubsectionItem>
                        );
                      },
                    )}
                    <div ref={loadMoreRecentsRef} />
                  </>
                : <p className='tw-mb-0 tw-pl-1 tw-text-s tw-tracking-wide tw-text-grey-500'>
                    <Trans
                      components={{
                        button: (
                          <button
                            className='tw-border-0 tw-bg-white tw-p-0 tw-font-medium tw-text-blue-500'
                            onClick={() => {
                              setIsAccordionOpen(false);
                              onSectionClick({ section: 'folders' });
                            }}
                          />
                        ),
                      }}
                      i18nKey='projectsDashboard.components.ProjectsDashboardSidebarMenu.folders.recents.empty'
                      ns='components'
                    />
                  </p>)}
            </Subsection>
          </div>
        )}
      </div>
    </SidebarMenu>
  );
}

ProjectsDashboardSidebarMenu.propTypes = {
  activeSection: PropTypes.string.isRequired,
  hasFolders: PropTypes.bool,
  onFolderClick: PropTypes.func.isRequired,
  onNewFolderClick: PropTypes.func.isRequired,
  onSectionClick: PropTypes.func.isRequired,
  selectedFolderId: PropTypes.string,
};

function SortableFavouriteUserFolder({
  folder,
  id,
  isActive,
  isDraggable,
  isSorting,
  onFolderClick,
}) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ disabled: !isDraggable, id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className={classNames(
        'tw-group tw-relative tw-z-[1] tw-rounded-lg',
        isSorting ?
          isDragging ? 'tw-z-[2] tw-bg-blue-025'
          : 'tw-bg-white'
        : 'tw-bg-white hover:tw-bg-blue-025',
      )}
      ref={setNodeRef}
      style={style}
    >
      <div
        className={classNames(
          'tw-absolute -tw-left-3 tw-flex tw-h-9 tw-w-5 tw-touch-none tw-items-center tw-justify-center tw-rounded-sm',
          isSorting ?
            isDragging ? 'tw-visible tw-cursor-grabbing tw-bg-blue-050'
            : 'hover-hover:tw-invisible'
          : 'tw-cursor-grab group-hover:tw-visible group-hover:tw-bg-blue-025 group-hover:hover:tw-visible group-hover:hover:tw-bg-blue-050 hover-hover:tw-invisible',
        )}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon
          className='[&_path]:tw-fill-grey-700'
          height={12}
          width={8}
        />
      </div>
      <SubsectionItem
        isActive={!isSorting && isActive}
        onClick={() =>
          onFolderClick({
            folderId: folder.id,
            section: 'favourites',
          })
        }
      >
        {folder.attributes.name}
      </SubsectionItem>
    </div>
  );
}

SortableFavouriteUserFolder.propTypes = {
  folder: resourceShape('folder').isRequired,
  id: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  isDraggable: PropTypes.bool,
  isSorting: PropTypes.bool,
  onFolderClick: PropTypes.func.isRequired,
};
